/**
 * OrgSurfaceContext — the org-unit pages' binding of the shared
 * detail-surface context.
 *
 * ONE BINDING FOR BOTH. A team and a department are the same record with a
 * different noun: they declare the same four panes (`context`, `access`,
 * `activity`, `reader`) and they share every pane body and the Documents tab
 * itself. Two contexts would mean `DocumentsTab` asking two questions to find
 * out where to put a document, so there is one — the SURFACES stay separate
 * (a team's arrangement is stored under its own key and must never be a
 * department's), only the context type is shared.
 *
 * NO SELECTION. Neither page selects a row into a shared detail container, so
 * this takes `DetailSurfaceApiWithoutSelection`: panes, the reader and tabs,
 * and no `select` that would open a pane neither surface declares.
 *
 * IT IS OPTIONAL ON PURPOSE. `useOrgSurface()` returns null outside the
 * provider, so `DocumentsTab` rendered anywhere else still opens a document —
 * through the `fileReader` side panel instead. Either way it is a side
 * surface, never a modal over the list it was opened from.
 */

import {
  DetailSurfaceApiWithoutSelection,
  createDetailSurfaceContext,
} from '../../shared/surface/DetailSurfaceContext';

/** The pane ids a team and a department both declare. */
export type OrgPanelId = 'context' | 'access' | 'activity' | 'reader';

/**
 * What the reader pane is showing.
 *
 * One shape, because an org unit has exactly one kind of readable thing: an
 * uploaded document, fetched as a blob through `useGlobalStreaming` and handed
 * to the same `ReaderComponent` every other surface reads with.
 */
export interface OrgReaderContent {
  /** Header line for the pane. */
  title: string;
  /** Still streaming — the pane shows a loader under the title. */
  loading?: boolean;
  citation?: any;
  /** The team or department the document hangs off, for the reader's actions. */
  resourceId: string;
  /** Download, so the reader's own button streams rather than re-fetching. */
  onDownload?: () => void;
}

export type OrgSurfaceApi = DetailSurfaceApiWithoutSelection<OrgPanelId, OrgReaderContent>;

const binding = createDetailSurfaceContext<OrgSurfaceApi>();

export const OrgSurfaceProvider = binding.Provider;

/** Null outside an org-unit surface — callers fall back to the side panel. */
export const useOrgSurface = binding.useSurface;

export default binding.Context;
